/**
 * An item's payload, read as data rather than as JSON.
 *
 * Objects are labelled rows, a list of like records (search results, demand rows) is a
 * table, a list of plain values is a line, urls are links and long text wraps. The raw
 * JSON stays one click away, because it is the receipt: what the tools actually stored.
 */
import { useState } from 'react';

/** Keys shown elsewhere in the drawer, or bookkeeping nobody reads. */
const HIDDEN = new Set(['fetch_log', 'woke', 'browser_job_ids']);
const MAX_DEPTH = 4;
const MAX_TABLE_COLUMNS = 6;
const MAX_ROWS = 50;

export function label(key: string): string {
  const words = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : key;
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isPlain = (v: unknown) => v === null || ['string', 'number', 'boolean'].includes(typeof v);

/** The columns for a list of records, when they share a shape that fits a table. */
export function tableColumns(rows: unknown[]): string[] | null {
  if (rows.length < 2 || !rows.every(isRecord)) return null;
  const cols: string[] = [];
  for (const r of rows as Record<string, unknown>[]) {
    for (const [k, v] of Object.entries(r)) {
      if (!isPlain(v)) return null;
      if (!cols.includes(k)) cols.push(k);
    }
  }
  return cols.length && cols.length <= MAX_TABLE_COLUMNS ? cols : null;
}

function Scalar({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === '')
    return <span className="text-gray-400">-</span>;
  if (typeof value === 'boolean') return <span>{value ? 'yes' : 'no'}</span>;
  if (typeof value === 'number') return <span className="tabular-nums">{value.toLocaleString()}</span>;
  const text = String(value);
  if (/^https?:\/\/\S+$/.test(text)) {
    return (
      <a
        href={text}
        target="_blank"
        rel="noopener noreferrer"
        className="text-indigo-600 dark:text-indigo-400 hover:underline break-all"
      >
        {text.replace(/^https?:\/\//, '')}
      </a>
    );
  }
  return (
    <span className={`whitespace-pre-line break-words ${text.length > 400 ? 'block max-h-48 overflow-y-auto' : ''}`}>
      {text}
    </span>
  );
}

function Value({ value, depth }: { value: unknown; depth: number }) {
  if (isPlain(value) || value === undefined) return <Scalar value={value} />;
  if (depth >= MAX_DEPTH) {
    return <code className="text-[11px] text-gray-500 break-all">{JSON.stringify(value)}</code>;
  }
  if (Array.isArray(value)) {
    if (!value.length) return <span className="text-gray-400">none</span>;
    if (value.every(isPlain)) {
      return (
        <span className="break-words">
          {value.map((v, i) => (
            <span key={i}>
              {i > 0 && ', '}
              <Scalar value={v} />
            </span>
          ))}
        </span>
      );
    }
    const cols = tableColumns(value);
    const rows = value.slice(0, MAX_ROWS);
    const more = value.length - rows.length;
    if (cols) {
      return (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-400">
                {cols.map(c => (
                  <th key={c} className="pr-3 pb-0.5 font-medium">
                    {label(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {(rows as Record<string, unknown>[]).map((r, i) => (
                <tr key={i} className="align-top">
                  {cols.map(c => (
                    <td key={c} className="pr-3 py-0.5">
                      <Scalar value={r[c]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {more > 0 && <div className="text-gray-400">…and {more} more</div>}
        </div>
      );
    }
    return (
      <ol className="space-y-1.5">
        {rows.map((v, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-gray-400 tabular-nums">{i + 1}.</span>
            <div className="min-w-0 flex-1">
              <Value value={v} depth={depth + 1} />
            </div>
          </li>
        ))}
        {more > 0 && <li className="text-gray-400">…and {more} more</li>}
      </ol>
    );
  }
  return <Fields data={value as Record<string, unknown>} depth={depth + 1} />;
}

function Fields({ data, depth }: { data: Record<string, unknown>; depth: number }) {
  const entries = Object.entries(data).filter(([k]) => depth > 0 || !HIDDEN.has(k));
  if (!entries.length) return <span className="text-gray-400">empty</span>;
  return (
    <dl className={`grid grid-cols-[minmax(5rem,auto)_1fr] gap-x-3 gap-y-1 ${depth > 0 ? 'pl-2 border-l border-gray-200 dark:border-gray-700' : ''}`}>
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-gray-500 dark:text-gray-400">{label(k)}</dt>
          <dd className="min-w-0 text-gray-800 dark:text-gray-200">
            <Value value={v} depth={depth} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default function PayloadView({ payload }: { payload: Record<string, unknown> }) {
  const [raw, setRaw] = useState(false);
  return (
    <div className="mt-1 text-xs bg-gray-50 dark:bg-gray-900 rounded p-2" data-testid="payload-view">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setRaw(r => !r)}
          className="text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          {raw ? 'Show as fields' : 'Raw JSON'}
        </button>
      </div>
      {raw ? (
        <pre className="text-xs overflow-x-auto whitespace-pre-wrap">{JSON.stringify(payload, null, 2)}</pre>
      ) : (
        <Fields data={payload} depth={0} />
      )}
    </div>
  );
}
